import { useState, type ReactNode } from "react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import type { DecideIn, DecisionRequest } from "../api";
import {
  buildBody,
  changeKind,
  failureMessage,
  needsConfirm,
  RATIONALE_MAX,
  scopePhrase,
  shortDate,
  slugify,
  validate,
  type DecideFailure,
  type FormState,
  type FormulaOption,
  type Kind,
  type MarketOption,
  type Outcome,
  type Problems,
  type TopicOption,
} from "../inbox";

const OUTCOME_LABELS: Record<Kind, Partial<Record<Outcome, string>>> = {
  coverage: {
    cover: "We support this topic",
    do_not_cover: "We don't support this topic",
    defer: "Not yet — defer",
  },
  ramp: { cover: "Publish this ramp", defer: "Not yet — defer" },
};

export function outcomeLabel(kind: Kind, outcome: Outcome): string {
  return OUTCOME_LABELS[kind][outcome] ?? "";
}

/**
 * An inline field error. It is an `<Alert>` and not red text: AGENTS boundary 14 makes
 * every error inverted (near-black, white text, red left edge), so one can never sit
 * unnoticed in the page's body colour.
 */
function FieldError({ id, children }: { id: string; children: ReactNode }) {
  return (
    <Alert id={id} variant="destructive" className="px-2.5 py-1.5">
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  );
}

/**
 * What the confirm dialog restates before anything is published: topic or formula,
 * outcome, scope and reason. Exported so it can be read without opening a portal.
 */
export function DecisionSummary({
  state,
  formulaLabel,
}: {
  state: FormState;
  formulaLabel: string;
}) {
  return (
    <dl className="space-y-2 text-sm text-ks-ink">
      <div>
        <dt className="text-xs font-medium text-ks-grey-1">
          {state.kind === "coverage" ? "Topic" : "Product formula"}
        </dt>
        <dd>{state.kind === "coverage" ? slugify(state.topic) : formulaLabel}</dd>
      </div>
      <div>
        <dt className="text-xs font-medium text-ks-grey-1">Decision</dt>
        <dd>{outcomeLabel(state.kind, state.outcome)}</dd>
      </div>
      <div>
        <dt className="text-xs font-medium text-ks-grey-1">Applies to</dt>
        <dd>{scopePhrase({ region: state.region, store: "*", channel: "*", locale: "*" })}</dd>
      </div>
      <div>
        <dt className="text-xs font-medium text-ks-grey-1">Reason</dt>
        <dd className="whitespace-pre-wrap">{state.rationale.trim()}</dd>
      </div>
    </dl>
  );
}

function Choice({ id, value, children }: { id: string; value: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <RadioGroupItem id={id} value={value} />
      <Label htmlFor={id} className="text-sm font-normal">
        {children}
      </Label>
    </div>
  );
}

/**
 * The decision form: one screen, three steps (UX Flow A).
 *
 * Every rule in `validate` mirrors the server's `_validate`, so the Record button says
 * why it is disabled in words. The server stays the authority: whatever it refuses
 * comes back as `serverProblems` and renders under the field it names.
 *
 * Nothing here is marked `data-nonmutating` except "Change", "More" and "Keep editing",
 * which change nothing. The decide controls are write actions (AGENTS boundary 11).
 */
export function DecisionForm({
  request,
  state,
  onChange,
  topics,
  topicsFailed,
  formulas,
  formulasFailed = false,
  markets,
  serverProblems = {},
  failure = null,
  pending,
  onSubmit,
  container,
}: {
  request: DecisionRequest;
  state: FormState;
  onChange: (next: FormState) => void;
  topics: readonly TopicOption[];
  topicsFailed: boolean;
  formulas: readonly FormulaOption[];
  /** The facts the formula list is read from could not be loaded: not the same as none existing. */
  formulasFailed?: boolean;
  markets: readonly MarketOption[];
  serverProblems?: Problems;
  failure?: DecideFailure | null;
  pending: boolean;
  onSubmit: (body: DecideIn) => void;
  /** The in-gate anchor the confirm dialog mounts into (see FactsTab's sheet anchor). */
  container: HTMLElement | null;
}) {
  const [confirming, setConfirming] = useState(false);
  const [changingScope, setChangingScope] = useState(state.region !== "*");
  const [showMore, setShowMore] = useState(false);

  const { reason, problems: found } = validate(state);
  const problems: Problems = { ...found, ...serverProblems };
  const body = buildBody(state);
  const set = (patch: Partial<FormState>) => onChange({ ...state, ...patch });

  const id = `inbox-${request.request_id}`;
  const slug = slugify(state.topic);
  const shownTopics = slug ? topics.filter((option) => option.topic.includes(slug)) : topics;
  const current = topics.find((t) => t.topic === slug);
  const formula = formulas.find((f) => f.id === state.formulaId);
  const deferring = state.outcome === "defer";
  const unitFailure = failure && failure.kind !== "fields" ? failureMessage(failure) : null;

  const submit = () => {
    if (!body) return;
    if (needsConfirm(state)) setConfirming(true);
    else onSubmit(body);
  };

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-ks-ink">What kind of decision?</legend>
        <RadioGroup
          aria-label="What kind of decision?"
          value={state.kind}
          onValueChange={(value) => onChange(changeKind(state, value as Kind))}
        >
          <Choice id={`${id}-kind-coverage`} value="coverage">
            Do we support this topic?
          </Choice>
          <Choice id={`${id}-kind-ramp`} value="ramp">
            Standard dosing ramp
          </Choice>
        </RadioGroup>
      </fieldset>

      {state.kind === "coverage" ? (
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-topic`}>Topic</Label>
          <Command shouldFilter={false} className="h-auto rounded-lg border border-input">
            <CommandInput
              id={`${id}-topic`}
              value={state.topic}
              onValueChange={(value) => set({ topic: value })}
              placeholder="Pick an existing topic or type a new one"
              aria-describedby={`${id}-topic-help`}
            />
            <CommandList className="max-h-40">
              {shownTopics.length === 0 && (
                <p className="py-6 text-center text-sm">
                  {topics.length === 0
                    ? "No topics yet. Type a new one."
                    : "No match. This will be saved as a new topic."}
                </p>
              )}
              <CommandGroup>
                {shownTopics.map((option) => (
                  <CommandItem
                    key={option.topic}
                    value={option.topic}
                    onSelect={() => set({ topic: option.topic })}
                  >
                    {option.topic}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          <p id={`${id}-topic-help`} className="text-xs text-ks-grey-1">
            {slug ? `Saved as: ${slug}` : "Topics are saved as lowercase words joined by hyphens."}
          </p>
          {topicsFailed && (
            <p className="text-xs text-ks-grey-1">
              We could not load existing topics. Check the spelling against Facts.
            </p>
          )}
          {current && (
            <p className="text-xs text-ks-grey-1">
              Currently: {current.covered ? "we support this" : "we don't support this"}
              {current.decidedAt ? ` (decided ${shortDate(current.decidedAt)})` : ""}. Saving
              replaces it.
            </p>
          )}
          {problems.topic && <FieldError id={`${id}-topic-error`}>{problems.topic}</FieldError>}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor={`${id}-formula`}>Product formula</Label>
            <Select value={state.formulaId} onValueChange={(value) => set({ formulaId: value })}>
              <SelectTrigger id={`${id}-formula`} className="w-full">
                <SelectValue placeholder="Pick a product formula">
                  {formulas.find((option) => option.id === state.formulaId)?.label}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {formulas.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formulasFailed ? (
              <p className="text-xs text-ks-grey-1">We could not load the product formulas. Try again shortly.</p>
            ) : (
              formulas.length === 0 && (
                <p className="text-xs text-ks-grey-1">No product formulas found in Protex yet.</p>
              )
            )}
            {problems.formula && (
              <FieldError id={`${id}-formula-error`}>{problems.formula}</FieldError>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            {(
              [
                ["start", "Start (ml)", state.start],
                ["target", "Target (ml)", state.target],
                ["step", "Step (ml)", state.step],
                ["interval", "Days between steps", state.interval],
              ] as const
            ).map(([key, label, value]) => (
              <div key={key} className="space-y-1.5">
                <Label htmlFor={`${id}-${key}`}>{label}</Label>
                <Input
                  id={`${id}-${key}`}
                  inputMode="decimal"
                  value={value}
                  aria-invalid={problems[key] ? true : undefined}
                  onChange={(event) => set({ [key]: event.target.value } as Partial<FormState>)}
                />
                {problems[key] && <FieldError id={`${id}-${key}-error`}>{problems[key]}</FieldError>}
              </div>
            ))}
          </div>
          {problems.ramp && <FieldError id={`${id}-ramp-error`}>{problems.ramp}</FieldError>}

          <div className="space-y-1.5">
            <Label htmlFor={`${id}-condition`}>Condition</Label>
            <Input
              id={`${id}-condition`}
              value={state.condition}
              placeholder="For example: if well tolerated"
              aria-invalid={problems.condition ? true : undefined}
              onChange={(event) => set({ condition: event.target.value })}
            />
            {problems.condition && (
              <FieldError id={`${id}-condition-error`}>{problems.condition}</FieldError>
            )}
          </div>
        </div>
      )}

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-ks-ink">Decision</legend>
        <RadioGroup
          aria-label="Decision"
          value={state.outcome}
          onValueChange={(value) => set({ outcome: value as Outcome })}
        >
          {(Object.keys(OUTCOME_LABELS[state.kind]) as Outcome[]).map((outcome) => (
            <Choice key={outcome} id={`${id}-outcome-${outcome}`} value={outcome}>
              {outcomeLabel(state.kind, outcome)}
            </Choice>
          ))}
        </RadioGroup>
        {problems.outcome && (
          <FieldError id={`${id}-outcome-error`}>{problems.outcome}</FieldError>
        )}
      </fieldset>

      <div className="space-y-1.5">
        <Label htmlFor={`${id}-why`}>Why?</Label>
        <Textarea
          id={`${id}-why`}
          aria-required="true"
          aria-invalid={problems.rationale ? true : undefined}
          aria-describedby={problems.rationale ? `${id}-why-error` : undefined}
          value={state.rationale}
          placeholder="The reason, in a sentence. Vox does not quote it."
          onChange={(event) => set({ rationale: event.target.value })}
        />
        {state.rationale.length > RATIONALE_MAX - 200 && (
          <p className="text-xs text-ks-grey-1">
            {state.rationale.length} of {RATIONALE_MAX} characters
          </p>
        )}
        {problems.rationale && (
          <FieldError id={`${id}-why-error`}>{problems.rationale}</FieldError>
        )}
      </div>

      <div className="space-y-1.5">
        <p className="flex flex-wrap items-center gap-2 text-sm text-ks-ink">
          <span>
            Applies to:{" "}
            {scopePhrase({ region: state.region, store: "*", channel: "*", locale: "*" })}
          </span>
          {!changingScope && (
            <Button
              type="button"
              variant="link"
              size="sm"
              data-nonmutating
              onClick={() => setChangingScope(true)}
            >
              Change
            </Button>
          )}
        </p>
        {changingScope && (
          <div className="space-y-1.5">
            <Label htmlFor={`${id}-market`}>Market</Label>
            <Select value={state.region} onValueChange={(value) => set({ region: value })}>
              <SelectTrigger id={`${id}-market`} className="w-full">
                <SelectValue>
                  {state.region === "*"
                    ? "Every market"
                    : (markets.find((market) => market.region === state.region)?.label ??
                      state.region)}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="*">Every market</SelectItem>
                {markets.map((market) => (
                  <SelectItem key={market.region} value={market.region}>
                    {market.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-nonmutating
          aria-expanded={showMore}
          onClick={() => setShowMore((open) => !open)}
        >
          {showMore ? "Less" : "More"} (linked research question)
        </Button>
        {showMore && (
          <div className="space-y-1.5">
            <Label htmlFor={`${id}-research`}>Linked research question</Label>
            <Input
              id={`${id}-research`}
              value={state.cortexQuestionId}
              onChange={(event) => set({ cortexQuestionId: event.target.value })}
            />
          </div>
        )}
      </div>

      {unitFailure && (
        <Alert variant="destructive">
          <AlertDescription>{unitFailure}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-1.5">
        <Button
          type="submit"
          className="w-full font-bold disabled:bg-ks-grey-4 disabled:text-ks-grey-1 disabled:opacity-100"
          disabled={reason !== null || pending}
          aria-describedby={reason ? `${id}-record-help` : undefined}
        >
          {pending ? "Saving…" : deferring ? "Defer request" : "Record decision"}
        </Button>
        {reason && (
          <p id={`${id}-record-help`} className="text-xs text-ks-grey-1">
            {reason}
          </p>
        )}
      </div>

      {container && (
        <Dialog open={confirming} onOpenChange={setConfirming}>
          <DialogContent container={container} className="gap-4 sm:max-w-[520px]">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-ks-ink">
                Record this decision?
              </DialogTitle>
              <DialogDescription>
                This publishes an answer that Vox will use in every reply from now on.
              </DialogDescription>
            </DialogHeader>
            <DecisionSummary state={state} formulaLabel={formula?.label ?? ""} />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                data-nonmutating
                onClick={() => setConfirming(false)}
              >
                Keep editing
              </Button>
              <Button
                type="button"
                className="font-bold"
                disabled={pending || !body}
                onClick={() => {
                  setConfirming(false);
                  if (body) onSubmit(body);
                }}
              >
                Record decision
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </form>
  );
}
